<template>
  <div class="view-container faq-container">
    <div class="extended-filter-block">
      <h2>Помощь с ЛК</h2>
    </div>
    <div class="card">
      <div class="search-header">Что вы хотите найти?</div>
      <div class="search-block">
        <a-input
            v-model:value="searchString"
            class="search-input"
            placeholder="Выдать доступы"
            :allowClear="true"
            @pressEnter.prevent="runSearch"
        >
          <template #suffix>
            <SearchOutlined class="search-icon" />
          </template>
        </a-input>

        <a-button @click="runSearch" class="primary-button">
          Найти
        </a-button>
      </div>
    </div>

    <div class="faq-main-wrapper">
      <div class="desktop-navigation">
        <FaqSideNavigation />
      </div>

      <div class="mobile-navigation">
        <FaqMobileNav />
      </div>

      <div class="card">
        <div class="faq-content">
          <section v-if="currentBlock">
            <h1>{{ currentBlock.mainHeader }}</h1>

            <template
                v-for="(item, i) in visibleItems"
                :key="`${routeState.block}-${routeState.section ?? 'all'}-${i}`"
            >
              <h2 v-if="item.type === 'h2'">{{ item.value }}</h2>

              <h2 v-else-if="item.type === 'section-title'">{{ item.value }}</h2>

              <h3 v-else-if="item.type === 'h3'">{{ item.value }}</h3>

              <p v-else-if="item.type === 'text'">{{ item.value }}</p>

              <small v-else-if="item.type === 'caption'" class="caption">
                {{ item.value }}
              </small>

              <p v-else-if="item.type === 'link'">
                <a
                    :href="item.href"
                    target="_blank"
                    rel="noopener"
                    class="green-link"
                >
                  {{ item.text }}
                </a>
              </p>

              <ul v-else-if="item.type === 'list'">
                <li v-for="(li, j) in item.items" :key="j">{{ li }}</li>
              </ul>
            </template>
          </section>

          <div v-if="profile">
            <div v-if="profile === 'superadmin'">
              Показаны варианты FAQ для всех ролей
            </div>

            <div class="admin-hint" v-if="profile === 'superadmin'">
              Сотрудник
            </div>

            <StaffFAQ v-if="profile === 'staff' || profile === 'superadmin'" />

            <div class="admin-hint" v-if="profile === 'superadmin'">
              Юридическое лицо
            </div>

            <AdminFAQ v-if="profile === 'admin' || profile === 'superadmin'" />

            <div class="admin-hint" v-if="profile === 'superadmin'">
              Физическое лицо
            </div>

            <PhysicalFAQ
                v-if="profile === 'private_person' || profile === 'superadmin'"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { SearchOutlined } from "@ant-design/icons-vue";

import PhysicalFAQ from "./PhysicalFAQ.vue";
import AdminFAQ from "./AdminFAQ.vue";
import StaffFAQ from "./StaffFAQ.vue";
import FaqSideNavigation from "@/components/FAQ/FaqSideNavigation.vue";
import FaqMobileNav from "@/components/FAQ/FaqMobileNav.vue";

import { faqBlocks } from "@/mixins/faqText";
import {
  DEFAULT_FAQ_BLOCK,
  blockHasSections,
  getFaqRouteLocation,
  normalizeFaqParams,
  readRouteParam,
} from "@/views/FAQ/faqRouteHelper";

const buildBlockModel = (block) => {
  const sections = new Map();
  const sharedItems = [];
  let currentSection = null;

  block.innerBlocks.forEach((item) => {
    if (item.type === "section") {
      currentSection = {
        value: item.value,
        title: item.title,
        items: item.title
            ? [{ type: "section-title", value: item.title }]
            : [],
      };

      sections.set(item.value, currentSection);
      return;
    }

    if (currentSection) {
      currentSection.items.push(item);
      return;
    }

    sharedItems.push(item);
  });

  return {
    ...block,
    sections,
    sharedItems,
    hasSections: sections.size > 0,
  };
};

const blockMap = new Map(
    faqBlocks.blocks.map((block) => [block.block, buildBlockModel(block)])
);

const createSearchRegExp = (value) => {
  return new RegExp(value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
};

const findRouteBySearch = (value) => {
  const matcher = createSearchRegExp(value);

  for (const block of faqBlocks.blocks) {
    const hasSections = block.innerBlocks.some((item) => item.type === "section");
    let currentSection = hasSections ? null : null;

    if (matcher.test(block.mainHeader ?? "")) {
      return getFaqRouteLocation(block.block, currentSection);
    }

    for (const item of block.innerBlocks) {
      if (item.type === "section") {
        currentSection = item.value;

        if (matcher.test(item.title ?? "")) {
          return getFaqRouteLocation(block.block, currentSection);
        }

        continue;
      }

      if (Array.isArray(item.items)) {
        for (const listItem of item.items) {
          if (typeof listItem === "string" && matcher.test(listItem)) {
            return getFaqRouteLocation(block.block, currentSection);
          }
        }
      }

      const haystack = item.value ?? item.text ?? "";

      if (typeof haystack === "string" && matcher.test(haystack)) {
        return getFaqRouteLocation(block.block, currentSection);
      }
    }
  }

  return null;
};

export default {
  name: "FAQ",
  components: {
    PhysicalFAQ,
    AdminFAQ,
    StaffFAQ,
    FaqSideNavigation,
    FaqMobileNav,
    SearchOutlined,
  },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const profile = computed(() => store.state.profile.profile?.role);
    const searchString = ref("");

    const routeState = computed(() => normalizeFaqParams(route.params));

    watch(
        [() => route.params.block, () => route.params.section],
        () => {
          const block = readRouteParam(route.params.block);
          const section = readRouteParam(route.params.section);

          if (block !== routeState.value.block || section !== routeState.value.section) {
            router.replace(
                getFaqRouteLocation(routeState.value.block, routeState.value.section)
            );
          }
        },
        { immediate: true }
    );

    const currentBlock = computed(
        () => blockMap.get(routeState.value.block) ?? blockMap.get(DEFAULT_FAQ_BLOCK)
    );

    const currentSection = computed(() => {
      if (!currentBlock.value?.hasSections) {
        return null;
      }

      return currentBlock.value.sections.get(routeState.value.section) ?? null;
    });

    const visibleItems = computed(() => {
      if (!currentBlock.value) {
        return [];
      }

      if (!currentBlock.value.hasSections) {
        return currentBlock.value.sharedItems;
      }

      if (!currentSection.value) {
        return [];
      }

      return [
        ...currentBlock.value.sharedItems,
        ...currentSection.value.items,
      ];
    });

    const runSearch = () => {
      const query = searchString.value.trim();

      if (!query) {
        router.push(getFaqRouteLocation(DEFAULT_FAQ_BLOCK));
        return;
      }

      const targetRoute = findRouteBySearch(query);

      if (targetRoute) {
        router.push(targetRoute);
      }
    };

    return {
      profile,
      searchString,
      routeState,
      currentBlock,
      visibleItems,
      runSearch,
    };
  },
};
</script>


<style lang="scss" scoped>
.admin-hint {
  border-bottom: 1px solid var(--main-color);
  border-top: 1px solid var(--main-color);
  padding: 10px;
  margin: 20px 0;
}

.view-container {
  padding-top: 0;
}

.search-header {
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 16px;
}

.search-block {
  display: flex;
  gap: 8px;
}

.faq-container {
  h1 {
    font-weight: 600;
    font-size: 24px;
    line-height: 120%;
  }

  h2 {
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
  }

  h3 {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
  }

  p, ul {
    font-weight: 400;
    font-size: 14px;
    line-height: 120%;
  }

  .green-link {
    color: var(--main-green);
    cursor: pointer;
  }

  .green-link:hover {
    color: var(--hover-green);
  }

  .caption {
    color: var(--grey-text);
  }

  .faq-main-wrapper {
    display: flex;
    gap: 16px;

    @media (max-width: $tablet-breakpoint) {
      flex-direction: column;
    }
  }

  .desktop-navigation {
    width: 255px;
    min-width: 255px;

     @media (max-width: $tablet-breakpoint) {
       display: none;
     }
  }

  .mobile-navigation {
    display: none;
    @media (max-width: $tablet-breakpoint) {
      display: block;
    }
  }
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 0;
    display: flex;
    column-gap: 25px;
    align-items: center;
    justify-content: space-between;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
}
</style>

<style>
.video_instruction {
  width: 100%;
}

@media (max-width: 1180px) {
  .video_instruction {
    width: 100%;
    height: 100%;
  }
}
</style>
